import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    MembershipTypeBreadcrumbs,
    MembershipTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsGeneralSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as MembershipTypeActions from "../../../actions/membershipTypes";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { LookupsLayout } from "../../index";

import "./MembershipTypePage.scss";

interface MembershipTypePageProps {
    form: Dtos.MembershipType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    membershipType: Dtos.MembershipType;
    lookups: Dtos.Lookup[];
    loadingMembershipType: boolean;
    loadMembershipTypeFailure: boolean;
    loadMembershipTypeErrorCode: string;
    savingMembershipType: boolean;
    saveMembershipTypeSuccess: boolean;
    saveMembershipTypeFailure: boolean;
    creatingMembershipType: boolean;
    createMembershipTypeSuccess: boolean;
    validatingMembershipType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearMembershipType: MembershipTypeActions.IMembershipTypeClearActionCreator;
    loadMembershipType: MembershipTypeActions.IMembershipTypeLoadByIdActionCreator;
    saveMembershipType: MembershipTypeActions.IMembershipTypeSaveActionCreator;
    createMembershipType: MembershipTypeActions.IMembershipTypeCreateActionCreator;
    validateMembershipType: MembershipTypeActions.IMembershipTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.membershipType";

const MembershipTypePage: FC<MembershipTypePageProps> = ({
    form,
    formState,
    formProperties,
    membershipType,
    lookups,
    loadingMembershipType,
    loadMembershipTypeFailure,
    loadMembershipTypeErrorCode,
    savingMembershipType,
    saveMembershipTypeSuccess,
    saveMembershipTypeFailure,
    creatingMembershipType,
    createMembershipTypeSuccess,
    validatingMembershipType,
    validationFailures,
    permissions,
    clearMembershipType,
    loadMembershipType,
    saveMembershipType,
    createMembershipType,
    validateMembershipType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { membershiptypesId } = useParams<{ membershiptypesId: string }>();
    const membershipTypeIdNumber = parseInt(membershiptypesId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.MembershipType = {} as Dtos.MembershipType;

        loadForm(
            reduxFormName,
            Object.assign({ ...membershipType }, extraData)
        );
    };


    // Load membership type
    useEffect(() => {
        if (
            membershipTypeIdNumber &&
            !membershipType &&
            !loadingMembershipType
        ) {
            setDrawerItems(SettingsGeneralSubMenu(permissions));
            setBreadcrumbs(MembershipTypeBreadcrumbs(membershipTypeIdNumber));
            loadMembershipType(membershipTypeIdNumber);
        } else if (
            !membershipTypeIdNumber &&
            !creatingMembershipType &&
            !createMembershipTypeSuccess
        ) {
            createMembershipType();
            setBreadcrumbs(MembershipTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (membershipType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        membershipTypeIdNumber,
        membershipType,
        setDrawerItems,
        SettingsGeneralSubMenu,
        permissions,
        setBreadcrumbs,
        MembershipTypeBreadcrumbs,
        loadMembershipType,
        createMembershipType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingMembershipType,
        creatingMembershipType,
        createMembershipTypeSuccess,
        MembershipTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMembershipTypeFailure) {
            navigate(`/error/${loadMembershipTypeErrorCode}`);
        }
    }, [loadMembershipTypeFailure, loadMembershipTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (membershipTypeIdNumber && membershipType?.value) {
            setBreadcrumbs(
                MembershipTypeBreadcrumbs(
                    membershipTypeIdNumber,
                    membershipType?.value
                )
            );
        }
    }, [
        membershipTypeIdNumber,
        membershipType,
        setBreadcrumbs,
        MembershipTypeBreadcrumbs,
    ]);

    // Reload membership type after saving
    useEffect(() => {
        if (!savingMembershipType && saveMembershipTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Membership type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/general/membershiptypes";
                navigate(url);
            } else if (!membershipTypeIdNumber) {
                let url: string = `/settings/general/membershiptypes/${membershipType?.id}`;
                navigate(url);
                clearMembershipType();
            }
        }
    }, [
        savingMembershipType,
        saveMembershipTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingMembershipType && saveMembershipTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingMembershipType, saveMembershipTypeFailure]);

    // Cleanup effect to clear the membership type on component unmount
    useEffect(() => {
        return () => {
            clearMembershipType();
            resetForm(reduxFormName);
        };
    }, [clearMembershipType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/general/membershiptypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.MembershipType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.MembershipType = {} as Dtos.MembershipType;

            await saveMembershipType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.MembershipType = {} as Dtos.MembershipType;

            if (form && hasBeenSubmitted && !validatingMembershipType) {
                await validateMembershipType(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.MembershipTypesUpdate,
                Dtos.Permission.MembershipTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingMembershipType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "membershipType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "membershipType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "membershipType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "membershipType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingMembershipType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <LookupsLayout
            loading={loadingMembershipType}
            permission={Dtos.Permission.MembershipTypesView}
        >
            {renderContent}
        </LookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<MembershipTypePageProps> => {
    let membershipType: Dtos.MembershipType | undefined = !(
        state.membershipTypes.formData instanceof Array
    )
        ? state.membershipTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        membershipType: membershipType!,
        loadingMembershipType:
            state.membershipTypes.loadState &&
            state.membershipTypes.loadState.status == RequestState.Pending,
        loadMembershipTypeFailure:
            state.membershipTypes.loadState &&
            state.membershipTypes.loadState.status == RequestState.Failure,
        loadMembershipTypeErrorCode: state.membershipTypes.loadState.errorCode,
        savingMembershipType:
            state.membershipTypes.saveState &&
            state.membershipTypes.saveState.status == RequestState.Pending,
        saveMembershipTypeSuccess:
            state.membershipTypes.saveState &&
            state.membershipTypes.saveState.status == RequestState.Success,
        saveMembershipTypeFailure:
            state.membershipTypes.saveState &&
            state.membershipTypes.saveState.status == RequestState.Failure,
        creatingMembershipType:
            state.membershipTypes.createState &&
            state.membershipTypes.createState.status == RequestState.Pending,
        createMembershipTypeSuccess:
            state.membershipTypes.createState &&
            state.membershipTypes.createState.status == RequestState.Success,
        validatingMembershipType:
            state.membershipTypes.validateState &&
            state.membershipTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.membershipType,
        formState: state.membershipTypes.formState,
        formProperties: state.membershipTypes.formProperties,
        validationFailures: membershipType
            ? state.membershipTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<MembershipTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadMembershipType: bindActionCreators(
            MembershipTypeActions.LoadMembershipTypeById,
            dispatch
        ),
        clearMembershipType: bindActionCreators(
            MembershipTypeActions.Clear,
            dispatch
        ),
        saveMembershipType: bindActionCreators(
            MembershipTypeActions.SaveMembershipType,
            dispatch
        ),
        createMembershipType: bindActionCreators(
            MembershipTypeActions.CreateMembershipType,
            dispatch
        ),
        validateMembershipType: bindActionCreators(
            MembershipTypeActions.ValidateMembershipType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MembershipTypePage);
